import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import Loader from "../../components/Loader";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialConverstionRateFilterData } from "../../utils/initialData";
import "../Contacts/Contact.css";
import ConverstionRateFilter from "./ConverstionRateFilter";
import ConversionRateModal from "./ConverstionRateModal";
import { CONVERSION_RATE_CATEGORIES, CONVERSION_RATE_CONFIG } from "./ConverstionRateType";

const ConversationRate = () => {
  const [config] = useAuth();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialConverstionRateFilterData;
  });

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRateKey, setSelectedRateKey] = useState(null);
  const [selectedRateLabel, setSelectedRateLabel] = useState("");
  const [detailedRate, setDetailedRate] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const buildQueryParams = (filters = {}) => {
    const params = {};
    const { created_start_date, created_end_date, created_at, bds } = filters;

    if (created_start_date) params.start_date = created_start_date;
    if (created_end_date) params.end_date = created_end_date;
    if (created_at) {
      const timeframeMap = {
        "Year To Date": "ytd",
        "Quarter To Date": "qtd",
        "Month To Date": "mtd",
      };
      params.timeframe = timeframeMap[created_at] || "ytd";
    }
    if (bds !== undefined && bds !== "") {
      params.bds = bds;
    }

    return params;
  };

  const fetchConversionRates = useCallback((filters) => {
    setLoading(true);
    const baseParams = buildQueryParams(filters);
    let queryString = "";
    
    Object.entries(baseParams).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        queryString += `${queryString ? "&" : ""}${key}=${encodeURIComponent(value)}`;
      }
    });
    
    const url = `${BASE_URL}/conversion-rates${queryString ? "?" + queryString : ""}`;
    
    axios
      .get(url, config)
      .then((res) => {
        setData(res.data?.data || {});
      })
      .catch((err) => {
        console.error("API Error:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [config]);

  const fetchDetailedRate = useCallback(async (filters) => {
    setModalLoading(true);
    
    // Build query string manually to ensure proper format
    const baseParams = buildQueryParams(filters);
    let queryString = "";
    
    Object.entries(baseParams).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        queryString += `${queryString ? "&" : ""}${key}=${encodeURIComponent(value)}`;
      }
    });
    
    // Add include_records and records_limit
    queryString += `${queryString ? "&" : ""}include_records=1&records_limit=100`;
    
    const url = `${BASE_URL}/conversion-rates${queryString ? "?" + queryString : ""}`;
    console.log("Fetching details with URL:", url);
    
    try {
      const res = await axios.get(url, config);
      console.log("Full API response:", res.data);
      setModalLoading(false);
      const responseData = res.data?.success ? res.data.data : res.data;
      return responseData || {};
    } catch (err) {
      console.error("API Error:", err);
      if (err.response) {
        console.error("Error response data:", err.response.data);
        console.error("Error response status:", err.response.status);
      }
      setModalLoading(false);
      return {};
    }
  }, [config]);

  useEffect(() => {
    fetchConversionRates(filterData);
  }, []);

  const onSuccess = (filters) => {
    setLoading(true);
    if (filters) {
      fetchConversionRates(filters);
    } else {
      fetchConversionRates(filterData);
    }
  };

  const handleViewDetails = useCallback(async (key, label) => {
    setSelectedRateKey(key);
    setSelectedRateLabel(label);
    setModalOpen(true);
    setDetailedRate(null);

    const detailedData = await fetchDetailedRate(filterData);
    console.log("API Response:", detailedData);
    console.log("Selected key:", key);
    
    const rateData = detailedData[key];
    console.log("Rate data for key:", rateData);
    
    if (rateData && typeof rateData === "object") {
      setDetailedRate(rateData);
    } else {
      // Fallback: if API returns simple rates, construct object from main data
      const fallbackRate = data[key] || "0%";
      setDetailedRate({
        rate: fallbackRate,
        data: {
          numerator_count: 0,
          denominator_count: 0,
          numerator_data: [],
          denominator_data: [],
        },
      });
    }
  }, [fetchDetailedRate, filterData, data]);

  // Get category label
  const getCategoryLabel = (categoryId) => {
    const category = CONVERSION_RATE_CATEGORIES[categoryId.toUpperCase()];
    return category ? category.label : categoryId;
  };

  // Parse rate value
  const parseRate = (rateStr) => {
    if (!rateStr) return "0%";
    if (typeof rateStr === "object" && rateStr.rate) return rateStr.rate;
    return rateStr;
  };

  return (
    <BaseLayout>
      <div className="contact-header mb-3">
        <p className="head-1 dark-H">Conversion Rates</p>

        <div className="flex gap-6 items-center">
          <ConverstionRateFilter
            filterData={filterData}
            onSetFilterData={(value) => {
              setFilterData(value);
            }}
            from="converstionrate"
            onCallApiAgain={(filters) => onSuccess(filters)}
          />
        </div>
      </div>

      <div className="light-bg-L pb-3 table-container" style={{ marginTop: "0px" }}>
        <div className="width table-info light-bg-L">
          {loading ? (
            <Loader />
          ) : (
            <table className="contact-table two-rows-static light-bg-L">
              <thead>
                <tr className="uppercase body-N dark-M">
                  {/* <th>Category</th> */}
                  <th>Metric</th>
                  {/* <th>Description</th> */}
                  <th>Rate</th>
                  {/* <th>Calculation</th> */}
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {CONVERSION_RATE_CONFIG.map((config, idx) => (
                  <tr key={idx} className="dark-H body-N">
                    {/* <td>
                      <span className="tags green-H body-S">
                        {getCategoryLabel(config.category)}
                      </span>
                    </td> */}
                    <td className="head-5">{config.label}</td>
                    {/* <td className="dark-M body-XS">{config.description}</td> */}
                    <td>
                      <span className="head-5 green-H">
                        {parseRate(data[config.key])}
                      </span>
                    </td>
                    {/* <td className="dark-M body-XS">
                      {config.numeratorLabel} / {config.denominatorLabel}
                    </td> */}
                    <td>
                      <button
                        className="action-button view-details-btn"
                        onClick={() => handleViewDetails(config.key, config.label)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <ConversionRateModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          rateKey={selectedRateKey}
          rateLabel={selectedRateLabel}
          detailedRate={detailedRate}
          loading={modalLoading}
        />
      </div>
    </BaseLayout>
  );
};

export default ConversationRate;